import React, { createContext, useCallback, useContext, useReducer } from 'react';

import { CheckInTemplateDetails } from 'models/templates/checkIn';

import { generateKey, insertAfterGivenPosition } from 'constants/helper';

const defaultEngagementTemplateState = CheckInTemplateDetails.fromJSON({});

const EngagementTemplateContext = createContext(defaultEngagementTemplateState);

const actions = {
  SET_DATA: 'SET_DATA',
  ADD_QUESTION: 'ADD_QUESTION',
  REMOVE_QUESTION: 'REMOVE_QUESTION',
  CLEAR_DATA: 'CLEAR_DATA',
  SET_QUESTIONS: 'SET_QUESTIONS',
};

const actionsReducer = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case actions.SET_DATA:
      return {
        ...state,
        ...payload.data,
      };
    case actions.ADD_QUESTION:
      return {
        ...state,
        templateQuestions: insertAfterGivenPosition(
          state.templateQuestions,
          payload.insertionPosition,
          { ...payload.question, uuid: generateKey(6) } // generating uuid to use as a unique identifier because the FE question does not have one, till they are saved and fetched from BE. And uuid is needed for the animation to run correctly
        ).map((ques, idx) => ({
          ...ques,
          displayOrder: idx + 1,
        })),
      };
    case actions.REMOVE_QUESTION:
      return {
        ...state,
        templateQuestions: state.templateQuestions.filter((ques, idx) => idx !== payload.index),
      };
    case actions.SET_QUESTIONS:
      return {
        ...state,
        templateQuestions: payload.questions,
      };
    case actions.CLEAR_DATA:
      return CheckInTemplateDetails.fromJSON({});

    default:
      return state;
  }
};

export const EngagementTemplateProvider = ({ children }) => {
  const [state, dispatch] = useReducer(actionsReducer, defaultEngagementTemplateState);

  const setData = useCallback(({ data }) => {
    dispatch({
      type: actions.SET_DATA,
      payload: { data },
    });
  }, []);

  const addQuestion = useCallback(({ question, insertionPosition }) => {
    dispatch({
      type: actions.ADD_QUESTION,
      payload: { question, insertionPosition },
    });
  }, []);

  const removeQuestion = useCallback(({ index }) => {
    dispatch({
      type: actions.REMOVE_QUESTION,
      payload: { index },
    });
  }, []);

  const setQuestions = useCallback(({ questions }) => {
    dispatch({
      type: actions.SET_QUESTIONS,
      payload: { questions },
    });
  }, []);

  const clearData = useCallback(() => {
    dispatch({
      type: actions.CLEAR_DATA,
    });
  }, []);

  return (
    <EngagementTemplateContext.Provider
      value={{
        state,
        setData,
        clearData,
        addQuestion,
        removeQuestion,
        setQuestions,
      }}
    >
      {children}
    </EngagementTemplateContext.Provider>
  );
};

export const useEngagementTemplate = () => useContext(EngagementTemplateContext);
